import React, {useMemo} from 'react';
import {
  Box,
  Flex,
  NumberInput,
  NumberInputField,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {IntentIcon} from '../Icons/Intent';
import {useIntentModelStore} from './IntentModeStore';

type ThresholdRow = {
  tier: 'high' | 'medium' | 'low';
  threshold?: number;
};

const thresholdColumnHelper = createColumnHelper<ThresholdRow>();

export const IntentTiers = () => {
  const {hasInitialized, thresholds, setThreshold} = useIntentModelStore();
  const data: ThresholdRow[] = useMemo(
    () => [
      {tier: 'low', threshold: thresholds.low},
      {tier: 'medium', threshold: thresholds.medium},
      {tier: 'high', threshold: thresholds.high},
    ],
    [thresholds]
  );

  const columns = useMemo(
    () => [
      thresholdColumnHelper.accessor('tier', {
        header: 'Tier',
        size: 200,
        cell: (info) => {
          const tier = info.getValue();
          return (
            <Flex alignItems="center" gap={4}>
              <IntentIcon tier={tier} />
              <Text textTransform="capitalize">{tier}</Text>
            </Flex>
          );
        },
      }),
      thresholdColumnHelper.accessor('threshold', {
        header: 'Score Threshold',
        size: 0,
        cell: (info) => {
          const tier = info.row.original.tier;
          const threshold = info.getValue();
          const isInvalid =
            hasInitialized && (threshold === undefined || threshold < 0);

          return (
            <Flex alignItems="center" gap="2" position="relative">
              <Box
                position="absolute"
                left="0"
                top="0"
                bottom="0"
                display="flex"
                alignItems="center"
                justifyContent="center"
                px="4"
              >
                &gt;
              </Box>
              <NumberInput
                isRequired
                isInvalid={isInvalid}
                maxW="250px"
                min={0}
                onChange={(_, valueAsNumber) => {
                  setThreshold(
                    tier,
                    !isNaN(valueAsNumber) ? valueAsNumber : undefined
                  );
                }}
                value={threshold ?? ''}
              >
                <NumberInputField color="kgray.300" pl="8" />
              </NumberInput>
            </Flex>
          );
        },
      }),
    ],
    [hasInitialized, setThreshold]
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.tier,
    defaultColumn: {
      minSize: 0,
      size: 150,
    },
  });

  return (
    <Flex
      bgColor="white"
      border="1px"
      borderColor="kgray.200"
      borderRadius="lg"
      direction="column"
      gap="6"
      h="fit-content"
      p="6"
      w="100%"
    >
      <Flex direction="column" gap="2">
        <Box fontSize="xl">Define Tiers</Box>
        <Box fontSize="sm" textColor="kgray.300">
          Set the thresholds that determine which scores fall into each tier.
        </Box>
      </Flex>
      <TableContainer>
        <Table
          variant="unstyled"
          sx={{
            td: {
              py: '2',
              px: '0',
            },
          }}
        >
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    color="kgray.300"
                    fontWeight="normal"
                    key={header.id}
                    w={header.getSize() ? `${header.getSize()}px` : 'auto'}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    w={
                      cell.getContext().column.getSize()
                        ? `${cell.getContext().column.getSize()}px`
                        : 'auto'
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Flex>
  );
};
